import React, { useEffect, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { checkIsSuperAdmin } from '../../utils/auth';
import { fetchMaintenanceConfig, getCurrentUserEmail, isWhitelisted } from '../../utils/maintenance';
import MaintenancePage from '../screens/MaintenancePage';

/**
 * Blocks the app for everyone except whitelisted testers and super admins while maintenance mode
 * is on, so a release can be tested against the live environment while everyone else is held on
 * the maintenance message.
 *
 * Two deliberate escape hatches:
 *  - any failure loading the config lets the user straight through (fail OPEN) — a flaky read must
 *    never be able to lock the whole platform out;
 *  - super admins are never blocked, otherwise turning maintenance on would also lock away the
 *    switch that turns it off.
 *
 * Signed-out visitors are not blocked here: they fall through to AuthWrapper's redirect to the hub
 * login, and are gated on the way back once their email is known.
 */
const MaintenanceGate = ({ children }) => {
  const [state, setState] = useState({ checking: true, config: null, email: null, blocked: false });

  useEffect(() => {
    let cancelled = false;

    const check = async () => {
      try {
        const config = await fetchMaintenanceConfig();
        if (!config.enabled) return { checking: false, config, email: null, blocked: false };

        const [email, superAdmin] = await Promise.all([getCurrentUserEmail(), checkIsSuperAdmin()]);
        const allowed = !email || superAdmin || isWhitelisted(email, config.whitelist);
        return { checking: false, config, email, blocked: !allowed };
      } catch (error) {
        console.error('Maintenance check failed, allowing access:', error);
        return { checking: false, config: null, email: null, blocked: false };
      }
    };

    check().then((next) => {
      if (!cancelled) setState(next);
    });

    return () => {
      cancelled = true;
    };
  }, []);

  if (state.checking) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  if (state.blocked) {
    return <MaintenancePage config={state.config} email={state.email} />;
  }

  return (
    <>
      {children}
      {state.config?.enabled && (
        // Reminder for the people who got through: everyone else is looking at the message.
        <div
          className="position-fixed bottom-0 end-0 m-3 px-3 py-2 rounded text-white"
          style={{ background: '#dc3545', zIndex: 2000, fontSize: '0.8rem' }}
        >
          Maintenance mode is ON
        </div>
      )}
    </>
  );
};

export default MaintenanceGate;
